import { Injectable } from '@angular/core';
import { AbPostHog } from 'app/abstract/services/posthog.abstract';
import { PostHogEventEnum } from 'enums/posthog-event.enum';
import {
  PostHogProperties,
  PostHogPersonProperties,
  PostHogGlobalProperties
} from './posthog-properties.types';
import { redactSensitiveProperties } from './posthog-properties';
import {
  iniciarClientePostHog,
  obterClientePostHog,
  resetarClientePostHog
} from './posthog-client';
import { esquecerIdentidadeAplicada } from './posthog-identidade';
import { montarPropriedadesGlobais } from './posthog-globals';

/**
 * Fachada injetável sobre o SDK. Não possui o cliente: a posse fica em posthog-client,
 * fora do grafo de DI, para que o SDK sobreviva às montagens e desmontagens da folha
 * Angular e inicialize uma única vez por carga de página.
 */
@Injectable({ providedIn: 'root' })
export class PostHogService extends AbPostHog {

  init(): Promise<void> {
    return iniciarClientePostHog();
  }

  isReady(): boolean {
    return obterClientePostHog() !== null;
  }

  identify(distinctId: string | number, properties?: PostHogPersonProperties): void {
    const cliente = obterClientePostHog();
    if (!cliente) {
      return;
    }
    try {
      const safe = properties ? redactSensitiveProperties(properties) : undefined;
      cliente.identify(String(distinctId), safe);
    } catch (err) {
      console.warn('[PostHogService] redator falhou; identify descartado', err);
    }
  }

  register(properties: PostHogGlobalProperties): void {
    const cliente = obterClientePostHog();
    if (!cliente) {
      return;
    }
    try {
      const safe = redactSensitiveProperties(properties);
      cliente.register(safe);
    } catch (err) {
      console.warn('[PostHogService] redator falhou; register descartado', err);
    }
  }

  capture(event: PostHogEventEnum | string, properties?: PostHogProperties): void {
    const cliente = obterClientePostHog();
    if (!cliente) {
      return;
    }
    try {
      const safe = properties ? redactSensitiveProperties(properties) : undefined;
      cliente.capture(String(event), safe);
    } catch (err) {
      console.warn('[PostHogService] redator falhou; evento descartado', err);
    }
  }

  reset(): void {
    // A identidade aplicada é esquecida mesmo com o SDK inativo: o estado de dedupe é
    // do módulo e não pode sobreviver a um logout.
    esquecerIdentidadeAplicada();
    resetarClientePostHog();
  }

  optIn(userId: string | number): void {
    this.persistirOptOut(userId, false);
    obterClientePostHog()?.opt_in_capturing();
  }

  optOut(userId: string | number): void {
    this.persistirOptOut(userId, true);
    obterClientePostHog()?.opt_out_capturing();
  }

  isOptedOut(userId: string | number): boolean {
    try {
      return localStorage.getItem(this.optOutStorageKey(userId)) === 'true';
    } catch {
      return false;
    }
  }

  private optOutStorageKey(userId: string | number): string {
    return `posthog_opt_out_${userId}`;
  }

  private persistirOptOut(userId: string | number, optedOut: boolean): void {
    try {
      localStorage.setItem(this.optOutStorageKey(userId), String(optedOut));
    } catch {
      void 0;
    }
  }
}
